import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { clientBookingsApi } from '../../api/clientBookingsApi';
import { useBackButton } from '../../hooks/useBackButton';
import { useMainButton } from '../../hooks/useMainButton';
import { ACTIVE_BOOKING_STATUSES } from '../../types/client';
import { ClientStatusBadge } from '../../components/client/bookings/ClientStatusBadge';
import { PaymentStatusBadge } from '../../components/shared/PaymentStatusBadge';
import { HeaderBar } from '../../components/client/ui/HeaderBar';
import { ErrorRetry } from '../../components/client/ui/ErrorRetry';
import { ScreenSkeleton } from '../../components/client/ui/ScreenSkeleton';
import type { ClientBookingDetails } from '../../types/client';

function getInitials(name: string): string {
  return name.split(' ').map((w) => w[0] ?? '').join('').toUpperCase().slice(0, 2);
}

interface InfoRowProps {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  label: string;
  value: React.ReactNode;
}

function InfoRow({ icon, iconBg, iconColor, label, value }: InfoRowProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px' }}>
      <div style={{
        width: 36, height: 36, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
        background: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 15, color: iconColor,
      }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--bl-text-secondary)', fontWeight: 500, marginBottom: 1 }}>
          {label}
        </div>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--bl-text)' }}>
          {value}
        </div>
      </div>
    </div>
  );
}

export default function BookingDetailsPage() {
  const { t } = useTranslation('customer');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<ClientBookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const formatDate = useCallback((date: string, time: string): string => {
    const d = new Date(`${date}T${time}:00`);
    const weekday = t(`bookings.dateHeader.weekdays.${d.getDay()}`);
    const month = t(`bookings.dateHeader.months.${d.getMonth()}`);
    return `${weekday}, ${d.getDate()} ${month}`;
  }, [t]);

  const goBack = useCallback(() => navigate('/bookings'), [navigate]);
  useBackButton(goBack);

  const fetchDetails = useCallback(async () => {
    if (!id) {
      navigate('/bookings', { replace: true });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await clientBookingsApi.getByUuid(id);
      setBooking(data);
    } catch {
      setError(t('bookings.details.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    void fetchDetails();
  }, [fetchDetails]);

  const isActive =
    booking !== null &&
    (ACTIVE_BOOKING_STATUSES as readonly string[]).includes(booking.status);

  const canCancel = booking?.canRequestCancel === true;
  const deadlinePassed = booking?.cancellationDeadlinePassed === true;

  const handleCancelClick = useCallback(() => {
    if (!booking) return;
    navigate(`/bookings/${booking.bookingUuid}/cancel`);
  }, [booking, navigate]);

  useMainButton(t('bookings.details.cancelRequestBtn'), handleCancelClick, canCancel);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <HeaderBar title={t('bookings.details.title')} />
        <ScreenSkeleton />
      </div>
    );
  }

  if (error !== null || booking === null) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <HeaderBar title={t('bookings.details.title')} />
        <ErrorRetry
          message={error ?? t('bookings.details.notFound')}
          onRetry={() => void fetchDetails()}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <HeaderBar
        title={t('bookings.details.title')}
        rightContent={<ClientStatusBadge status={booking.status} />}
      />

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 24px' }}>

        {/* Hero karta: provider + servis + sana + vaqt */}
        <div style={{
          margin: '12px 16px 0',
          borderRadius: 'var(--bl-radius-lg)',
          background: 'var(--bl-surface)',
          border: '1px solid var(--bl-divider)',
          overflow: 'hidden',
        }}>
          {/* Provider + servis block */}
          <div style={{
            padding: '14px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            borderBottom: '1px solid var(--bl-divider)',
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
              background: 'rgba(51,144,236,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, fontWeight: 700, color: 'var(--bl-primary)',
            }}>
              {getInitials(booking.providerName)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 15, fontWeight: 600, color: 'var(--bl-text)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {booking.serviceName}
              </div>
              <div style={{ fontSize: 13, color: 'var(--bl-text-secondary)', marginTop: 2 }}>
                {booking.providerName}
              </div>
            </div>
          </div>

          <InfoRow
            icon={<CalendarOutlined />}
            iconBg="rgba(217,119,6,0.10)"
            iconColor="rgba(217,119,6,1)"
            label={t('bookings.details.date')}
            value={formatDate(booking.date, booking.time)}
          />
          <div style={{ height: 1, background: 'var(--bl-divider)', margin: '0 14px' }} />
          <InfoRow
            icon={<ClockCircleOutlined />}
            iconBg="rgba(31,139,76,0.10)"
            iconColor="rgba(31,139,76,1)"
            label={t('bookings.details.time')}
            value={t('bookings.details.timeWithDuration', { time: booking.time, duration: booking.durationMinutes })}
          />
        </div>

        {/* Details karta: narx + izoh */}
        <div style={{
          margin: '8px 16px 0',
          borderRadius: 'var(--bl-radius-lg)',
          background: 'var(--bl-surface)',
          border: '1px solid var(--bl-divider)',
          overflow: 'hidden',
        }}>
          <InfoRow
            icon={<DollarOutlined />}
            iconBg="rgba(51,144,236,0.10)"
            iconColor="var(--bl-primary)"
            label={t('bookings.details.price')}
            value={
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>{booking.paymentLabel}</span>
                {booking.price > 0 && (
                  <PaymentStatusBadge status={booking.paymentStatus} size="sm" />
                )}
              </div>
            }
          />
          {booking.note !== null && booking.note !== '' && (
            <>
              <div style={{ height: 1, background: 'var(--bl-divider)', margin: '0 14px' }} />
              <InfoRow
                icon={<FileTextOutlined />}
                iconBg="var(--bl-surface)"
                iconColor="var(--bl-text-secondary)"
                label={t('bookings.details.notes')}
                value={booking.note}
              />
            </>
          )}
        </div>

        {/* Deadline passed warning */}
        {isActive && deadlinePassed && !canCancel && (
          <div style={{
            margin: '8px 16px 0',
            borderRadius: 'var(--bl-radius)',
            background: 'rgba(217,119,6,0.08)',
            border: '1px solid rgba(217,119,6,0.25)',
            padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
              background: 'rgba(217,119,6,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, color: 'rgba(217,119,6,1)',
            }}>
              <ClockCircleOutlined />
            </div>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'rgba(217,119,6,1)' }}>
              {t('bookings.details.deadlinePassedWarning')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
